import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import TrackVisibility from 'react-on-screen';
import { AdSlot } from 'react-dfp';
// import { Link } from 'react-router-dom';
import Spinner from '../../../common/Spinner/Spinner';
import SuperSimSponsoredNewsTeaser from '../../../common/editorialAd/SuperSimSponsoredNewsTeaser';
import { placeSuperSimSponsoredNews } from '../helpers/superSimSponsoredNews';
import shouldUseGam from '../../../helpers/shouldUseGam';
import { useOptionalCookies } from '../../../helpers/optionalCookies';
import NewsHomeMostRelevantComponent from './NewsHomeMostRelevantComponent';
import NewsHomeRelevantLeftComponent from './NewsHomeRelevantLeftComponent';
import NewsHomeColumnComponent from './NewsHomeColumnComponent';
import NewsHomeColumnReutersComponent from './NewsHomeColumnReutersComponent';

const homeHeroBanner = {
    href: 'https://byddahruj.com.br/',
    src: 'https://a1media.antena1.com.br/image/banner/home/dahruj-banner-campanha-may-2026.jpeg',
    alt: 'BYD Dahruj - Campinas e regiao',
};

const homeHeroBannerConfig = {
    // This slot lives directly under the GAM root ad unit `ca-pub-2148260700418961`.
    // This slot must use the standard GAM network code + ad unit path format.
    // The global DFP provider still points at `/antena_1`, which does not exist for this slot.
    dfpNetworkId: '60229764/ca-pub-2148260700418961',
    adUnit: 'a1nova-home-hero',
    sizes: [[1496, 250], [1440, 250], [970, 250], [728, 90], [320, 100], [320, 50]],
    sizeMapping: [
        { viewport: [1496, 0], sizes: [[1496, 250]] },
        { viewport: [1440, 0], sizes: [[1440, 250], [970, 250], [728, 90]] },
        { viewport: [970, 0], sizes: [[970, 250], [728, 90]] },
        { viewport: [728, 0], sizes: [[728, 90]] },
        { viewport: [320, 0], sizes: [[320, 100], [320, 50]] },
        { viewport: [0, 0], sizes: [] },
    ],
    shouldRefresh: true,
};

function NewsHomeComponent({
    newsHomeData = [],
    pinnedRightColumnNews = null,
    reutersNewsData = [],
    breakpointReducer = {},
    isPro = false,
    profileInitialized = false,
    getNewsHomeDataIfNeeded = () => { },
    getNewsDataIfNeeded = () => { },
    countNewsView = () => { },
}) {
    const consent = useOptionalCookies();
    const shouldUseGamAds = shouldUseGam();

    useEffect(() => {
        getNewsHomeDataIfNeeded();

        getNewsDataIfNeeded();
    }, [getNewsHomeDataIfNeeded, getNewsDataIfNeeded]);

    const [showHero, setShowHero] = useState(false);
    const [heroHasCreative, setHeroHasCreative] = useState(false);
    const [heroResolved, setHeroResolved] = useState(false);

    useEffect(() => {
        if (profileInitialized) {
            const canShowHero = !isPro;

            setShowHero(canShowHero);
            setHeroResolved(false);
            setHeroHasCreative(shouldUseGamAds ? false : canShowHero);

            if (shouldUseGamAds && !canShowHero) {
                setHeroResolved(true);
            }
        } else {
            setShowHero(false);
            setHeroHasCreative(false);
            setHeroResolved(false);
        }
    }, [profileInitialized, isPro, shouldUseGamAds]);

    const homeNews = newsHomeData || [];
    const reutersNews = reutersNewsData || [];
    const pinnedNews = pinnedRightColumnNews || null;

    const slugUsed = new Set();

    if (pinnedNews?.slug) {
        slugUsed.add(pinnedNews.slug);
    }

    const uniqueHomeNews = homeNews.filter((item) => {
        if (!item.slug || slugUsed.has(item.slug)) return false;

        slugUsed.add(item.slug);

        return true;
    });

    const {
        pinnedNews: pinnedSuperSimSponsoredNews,
        newsItems: homeNewsWithSponsoredNews,
    } = placeSuperSimSponsoredNews(uniqueHomeNews);

    const relevantLeftNews = homeNewsWithSponsoredNews.slice(6);

    const mostRelevantNews = homeNewsWithSponsoredNews.slice(0, 1);

    const columnNews = homeNewsWithSponsoredNews.slice(1, 7);

    const allSlugsUsed = new Set([
        ...mostRelevantNews.map((n) => n.slug),
        ...relevantLeftNews.map((n) => n.slug),
        ...columnNews.map((n) => n.slug),
    ]);

    const reutersBlockNews = [
        ...(pinnedNews ? [pinnedNews] : []),
        ...reutersNews.filter((n) => !allSlugsUsed.has(n.slug) && n.slug !== pinnedNews?.slug),
    ].slice(0, 5);

    if (!homeNews || homeNews.length === 0) {
        return <Spinner />;
    }

    const shouldRenderGamHero = showHero && (!heroResolved || heroHasCreative);
    const heroContainerClass = heroHasCreative
        ? 'tw-flex tw-justify-center tw-overflow-x-auto'
        : 'tw-h-px tw-overflow-hidden tw-opacity-0';

    return (
        <React.Fragment>
            {consent && (shouldUseGamAds ? shouldRenderGamHero : showHero) && (
                <div className={`tw-mx-auto tw-w-full tw-container tw-px-5 ${heroHasCreative ? 'tw-my-2' : ''}`}>
                    {shouldUseGamAds ? (
                        <div className={heroContainerClass}>
                            <AdSlot
                                dfpNetworkId={homeHeroBannerConfig.dfpNetworkId}
                                adUnit={homeHeroBannerConfig.adUnit}
                                sizes={homeHeroBannerConfig.sizes}
                                sizeMapping={homeHeroBannerConfig.sizeMapping}
                                className="homeHeroAdSlot"
                                onSlotRender={({ event }) => {
                                    setHeroHasCreative(!event.isEmpty);
                                    setHeroResolved(true);
                                }}
                                shouldRefresh={() => homeHeroBannerConfig.shouldRefresh}
                            />
                        </div>
                    ) : (
                        <a
                            href={homeHeroBanner.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tw-block tw-w-full"
                        >
                            <img
                                src={homeHeroBanner.src}
                                alt={homeHeroBanner.alt}
                                className="tw-block tw-w-full tw-h-auto"
                                loading="eager"
                            />
                        </a>
                    )}
                </div>
            )}

            {pinnedSuperSimSponsoredNews && (
                <div className="tw-mx-auto tw-container tw-px-5 md:tw-hidden">
                    <SuperSimSponsoredNewsTeaser news={pinnedSuperSimSponsoredNews} />
                </div>
            )}

            <div className={`tw-grid tw-gap-10 md:tw-mt-0 tw-mx-auto tw-container tw-grid-cols-1 md:tw-grid-cols-12 tw-justify-between noticias-destaques tw-px-5 ${heroHasCreative ? 'news-home-grid--with-hero' : 'news-home-grid--without-hero'}`}>

                {/* materias relevantes */}
                <div className="tw-order-2 md:tw-order-1 md:tw-col-span-3 materia-relevantes-2">
                    {pinnedSuperSimSponsoredNews && (
                        <SuperSimSponsoredNewsTeaser
                            className="superSimSponsoredNewsTeaser--sidebar"
                            news={pinnedSuperSimSponsoredNews}
                        />
                    )}

                    <TrackVisibility>
                        {({ isVisible }) => (
                            <NewsHomeRelevantLeftComponent
                                dataMostRelevantNews={relevantLeftNews}
                                isVisible={isVisible}
                                countNewsView={countNewsView}
                            />
                        )}
                    </TrackVisibility>
                </div>

                {/* materias relevantes super destaque + músicas */}
                <div className=" md:tw-col-span-6 md:tw-order-2 materia-relevantes">
                    <TrackVisibility>
                        {({ isVisible }) => (
                            <NewsHomeMostRelevantComponent
                                dataMostRelevantNews={mostRelevantNews}
                                breakpointReducer={breakpointReducer}
                                isVisible={isVisible}
                                countNewsView={countNewsView}
                            />
                        )}
                    </TrackVisibility>

                    <TrackVisibility>
                        {({ isVisible }) => (
                            <NewsHomeColumnComponent
                                dataMostRelevantNews={columnNews}
                                breakpointReducer={breakpointReducer}
                                isVisible={isVisible}
                                countNewsView={countNewsView}
                            />
                        )}
                    </TrackVisibility>
                </div>

                {/* materias reuters */}
                <div className="tw-hidden md:tw-block tw-order-3 md:tw-order-3 md:tw-col-span-3 materia-reuters">
                    <TrackVisibility>
                        {({ isVisible }) => (
                            <NewsHomeColumnReutersComponent
                                countNewsView={countNewsView}
                                isVisible={isVisible}
                                breakpointReducer={breakpointReducer}
                                dataNewsReuters={reutersBlockNews}
                            />
                        )}
                    </TrackVisibility>
                </div>
            </div>
        </React.Fragment>
    );
}

NewsHomeComponent.propTypes = exact({
    newsHomeData: PropTypes.array.isRequired,
    pinnedRightColumnNews: PropTypes.object,
    reutersNewsData: PropTypes.array,
    breakpointReducer: PropTypes.object.isRequired,
    isPro: PropTypes.bool,
    profileInitialized: PropTypes.bool.isRequired,
    getNewsHomeDataIfNeeded: PropTypes.func.isRequired,
    getNewsDataIfNeeded: PropTypes.func.isRequired,
    countNewsView: PropTypes.func.isRequired,
});

export default NewsHomeComponent;
